import { useCallback, useEffect, useState } from 'react'
import type { ServerApiClient } from '../../../../data/ServerApiClient'
import { trimStr } from '../cfoFormat'
import type {
  CeoCockpitPeriodRun,
  CeoCockpitResponse,
  CeoCockpitSnapshotIndex,
} from './ceoCockpitTypes'

export type CeoCockpitLoadStatus = 'loading' | 'ready' | 'not_ready' | 'error'

export type UseCeoCockpitResult = {
  payload: CeoCockpitResponse | null
  status: CeoCockpitLoadStatus
  error: string | null
  periodRun: CeoCockpitPeriodRun | null
  reload: () => void
}

type CacheEntry = {
  value: CeoCockpitResponse | null
  error: any | null
  updatedAt: number
  status: CeoCockpitLoadStatus
}

const CACHE_TTL_MS = 60_000
// A 428 means a real cold snapshot build is running. Retry long enough to
// bridge the observed sub-10-second build window, then stop presenting an
// infinite skeleton and expose the normal Retry action.
const PREPARING_RETRY_DELAYS_MS = [1_000, 1_500, 2_500, 4_000, 6_000] as const
const ceoCockpitCache = new Map<string, CacheEntry>()

export function invalidateCeoCockpitCache(workbookId?: string | null) {
  const wb = trimStr(workbookId)
  if (wb) {
    for (const key of ceoCockpitCache.keys()) {
      if (key === wb || key.startsWith(`${wb}::`)) ceoCockpitCache.delete(key)
    }
    return
  }
  ceoCockpitCache.clear()
}

function isPrecondition(error: any): boolean {
  return Number(error?.status || 0) === 428
}

export function useCeoCockpit(
  appApi: ServerApiClient,
  workbookId: string | null,
  snapshotDate?: string | null,
): UseCeoCockpitResult {
  const date = trimStr(snapshotDate) || null
  const requestedKey = cacheKeyFor(trimStr(workbookId), date)
  const [payload, setPayload] = useState<CeoCockpitResponse | null>(null)
  const [payloadKey, setPayloadKey] = useState(requestedKey)
  const [status, setStatus] = useState<CeoCockpitLoadStatus>(() => (trimStr(workbookId) ? 'loading' : 'error'))
  const [error, setError] = useState<string | null>(null)
  const [periodRun, setPeriodRun] = useState<CeoCockpitPeriodRun | null>(null)
  const [periodRunKey, setPeriodRunKey] = useState(requestedKey)
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => {
    invalidateCeoCockpitCache(workbookId)
    setNonce((value) => value + 1)
  }, [workbookId])

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let preparingAttempt = 0
    const wb = trimStr(workbookId)
    if (!wb) {
      setPayload(null)
      setPayloadKey(cacheKeyFor(wb, date))
      setStatus('error')
      setError('Missing workbook')
      return
    }

    const cacheKey = cacheKeyFor(wb, date)
    const now = Date.now()
    const cached = ceoCockpitCache.get(cacheKey)
    if (!date && cached?.value && now - cached.updatedAt < CACHE_TTL_MS && cached.status === 'ready') {
      setPayload(cached.value)
      setPayloadKey(cacheKey)
      setStatus('ready')
      setError(null)
      return
    }
    // Never leave a payload from a different date on screen while an exact
    // historical request is loading.
    setPayload(cached?.value ?? null)
    setPayloadKey(cacheKey)
    setPeriodRun(null)
    setPeriodRunKey(cacheKey)

    const load = typeof appApi.getQuickBooksCeoCockpit === 'function'
      ? () => appApi.getQuickBooksCeoCockpit(wb, date ? { date } : undefined)
      : null
    if (!load) {
      setStatus('error')
      setError('CEO Cockpit API is not available')
      return
    }

    const request = () => {
      load()
        .then((next) => {
        if (cancelled) return
        ceoCockpitCache.set(cacheKey, { value: next, error: null, updatedAt: Date.now(), status: 'ready' })
        setPayload(next)
        setPayloadKey(cacheKey)
        setStatus('ready')
        setError(null)
        })
        .catch((err: any) => {
        if (cancelled) return
        const lastGood = ceoCockpitCache.get(cacheKey)?.value || cached?.value || null
        if (isPrecondition(err)) {
          setPayload(lastGood)
          setPayloadKey(cacheKey)
          setStatus('not_ready')
          setError(null)
          ceoCockpitCache.set(cacheKey, { value: lastGood, error: err, updatedAt: Date.now(), status: 'not_ready' })
          const delay = PREPARING_RETRY_DELAYS_MS[preparingAttempt]
          if (delay !== undefined) {
            preparingAttempt += 1
            retryTimer = setTimeout(request, delay)
            return
          }
          const message = 'CEO Cockpit preparation is taking longer than expected.'
          setStatus('error')
          setError(message)
          ceoCockpitCache.set(cacheKey, { value: lastGood, error: err, updatedAt: Date.now(), status: 'error' })
          return
        }
        setPayload(lastGood)
        setPayloadKey(cacheKey)
        setStatus('error')
        setError(err?.message || String(err) || 'CEO Cockpit failed to load')
        ceoCockpitCache.set(cacheKey, { value: lastGood, error: err, updatedAt: Date.now(), status: 'error' })
        })
    }

    const startPeriodRun = typeof appApi.startQuickBooksCeoCockpitPeriodRun === 'function'
      ? () => appApi.startQuickBooksCeoCockpitPeriodRun(wb, date as string)
      : null
    const getPeriodRun = typeof appApi.getQuickBooksCeoCockpitPeriodRun === 'function'
      ? (runId: string) => appApi.getQuickBooksCeoCockpitPeriodRun(wb, runId)
      : null

    const acceptPeriodRun = (run: CeoCockpitPeriodRun) => {
      if (cancelled) return
      setPeriodRun(run)
      setPeriodRunKey(cacheKey)
      if (run.status === 'ready') {
        ceoCockpitCache.delete(cacheKey)
        setStatus('loading')
        request()
        return
      }
      if (run.status === 'failed') {
        const message = run.error || 'The selected period could not be prepared.'
        setPayload(null)
        setPayloadKey(cacheKey)
        setStatus('error')
        setError(message)
        return
      }
      setPayload(null)
      setPayloadKey(cacheKey)
      setStatus('not_ready')
      setError(null)
      if (!getPeriodRun) return
      retryTimer = setTimeout(() => {
        getPeriodRun(run.run_id)
          .then(acceptPeriodRun)
          .catch((err: any) => {
            if (cancelled) return
            setStatus('error')
            setError(err?.message || String(err) || 'Period progress could not be loaded')
          })
      }, 1_000)
    }

    setStatus('loading')
    if (date && startPeriodRun && getPeriodRun) {
      startPeriodRun()
        .then(acceptPeriodRun)
        .catch((err: any) => {
          if (cancelled) return
          setStatus('error')
          setError(err?.message || String(err) || 'The selected period could not be started')
        })
    } else {
      request()
    }

    return () => {
      cancelled = true
      if (retryTimer !== null) clearTimeout(retryTimer)
    }
  }, [appApi, workbookId, date, nonce])

  const keyMatches = payloadKey === requestedKey
  return {
    payload: keyMatches ? payload : null,
    status: keyMatches ? status : 'loading',
    error: keyMatches ? error : null,
    periodRun: keyMatches && periodRunKey === requestedKey ? periodRun : null,
    reload,
  }
}

function cacheKeyFor(workbookId: string, date: string | null): string {
  return `${workbookId}::${date || 'latest'}`
}

export type UseCeoCockpitSnapshotsResult = {
  index: CeoCockpitSnapshotIndex | null
  error: string | null
}

/** Discover completed dates for calendar suggestions and retained-audit access. */
export function useCeoCockpitSnapshots(
  appApi: ServerApiClient,
  workbookId: string | null,
): UseCeoCockpitSnapshotsResult {
  const [index, setIndex] = useState<CeoCockpitSnapshotIndex | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    const load = typeof appApi.getQuickBooksCeoCockpitSnapshots === 'function'
      ? () => appApi.getQuickBooksCeoCockpitSnapshots(wb)
      : null
    if (!wb || !load) {
      setIndex(null)
      setError(null)
      return
    }
    load()
      .then((next) => {
        if (cancelled) return
        setIndex(next)
        setError(null)
      })
      .catch((err: any) => {
        if (cancelled) return
        setIndex(null)
        setError(err?.message || String(err) || 'Snapshot history failed to load')
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId])

  return { index, error }
}
